import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const EditCarDetails = () => {
  const [cars, setCars] = useState([]); // Stores all cars
  const [selectedCarId, setSelectedCarId] = useState(""); // Tracks the selected car ID
  const [carData, setCarData] = useState(null); // Stores the selected car's data
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false); // Tracks image upload progress

  // Fetch all cars when the component mounts
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "cars"));
        const carList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCars(carList);
      } catch (error) {
        console.error("Error fetching cars:", error);
      }
    };

    fetchCars();
  }, []);

  // Fetch selected car details
  const handleCarSelect = async (carId) => {
    setSelectedCarId(carId);
    if (carId) {
      try {
        setLoading(true);
        const carRef = doc(db, "cars", carId);
        const carSnap = await getDoc(carRef);
        if (carSnap.exists()) {
          setCarData(carSnap.data());
        } else {
          console.error("Car not found");
        }
      } catch (error) {
        console.error("Error fetching car details:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setCarData(null);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCarData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle adding features to the features array
  const handleAddFeature = () => {
    const newFeature = prompt("Enter a new feature:");
    if (newFeature && carData) {
      setCarData((prev) => ({
        ...prev,
        features: [...(prev.features || []), newFeature],
      }));
    }
  };

  // Handle picture uploads
  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const storageRef = ref(storage, `cars/${selectedCarId}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    setUploading(true);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`Upload is ${progress}% done`);
      },
      (error) => {
        console.error("Upload failed:", error);
        setUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setCarData((prev) => ({
          ...prev,
          pictures: [...(prev.pictures || []), downloadURL],
        }));
        setUploading(false);
        alert("Picture uploaded successfully!");
      }
    );
  };

  // Handle form submission to update car details
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCarId || !carData) {
      alert("Please select a car and make changes before submitting.");
      return;
    }

    try {
      setLoading(true);
      const carRef = doc(db, "cars", selectedCarId);
      await updateDoc(carRef, carData);
      alert("Car details updated successfully!");
    } catch (error) {
      console.error("Error updating car details:", error);
      alert("Failed to update car details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "20px", fontFamily: "'Roboto', sans-serif" }}>
      <h1 style={{ textAlign: "center", marginBottom: "20px", color: "#007bff" }}>
        Edit Car Details
      </h1>

      {/* Dropdown to select a car */}
      <div style={{ marginBottom: "20px", textAlign: "center" }}>
        <label htmlFor="carSelect" style={{ marginRight: "10px", fontSize: "1.2em" }}>
          Select a Car:
        </label>
        <select
          id="carSelect"
          value={selectedCarId}
          onChange={(e) => handleCarSelect(e.target.value)}
          style={{
            padding: "10px",
            fontSize: "1.1em",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        >
          <option value="">-- Choose a car --</option>
          {cars.map((car) => (
            <option key={car.id} value={car.id}>
              {car.carName || "Unnamed Car"}
            </option>
          ))}
        </select>
      </div>

      {/* Show loading or form */}
      {loading ? (
        <p style={{ textAlign: "center", fontSize: "1.2em" }}>Loading...</p>
      ) : carData ? (
        <form
          onSubmit={handleSubmit}
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            padding: "20px",
            backgroundColor: "#f8f9fa",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="carName" style={{ fontWeight: "bold", color: "black" }}>
              Car Name:
            </label>
            <input
              id="carName"
              type="text"
              name="carName"
              value={carData.carName || ""}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
              }}
              required
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="price" style={{ fontWeight: "bold", color: "black" }}>
              Price per Day:
            </label>
            <input
              id="price"
              type="number"
              name="price"
              value={carData.price || ""}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
              }}
              required
            />
          </div>

          {/* Features Section */}
          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="features" style={{ fontWeight: "bold", color: "black" }}>
              Features:
            </label>
            <ul style={{ paddingLeft: "20px" }}>
              {(carData.features || []).map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={handleAddFeature}
              style={{
                padding: "10px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Add Feature
            </button>
          </div>

          {/* Pictures Section */}
          <div style={{ marginBottom: "15px" }}>
            <label htmlFor="pictures" style={{ fontWeight: "bold", color: "black" }}>
              Pictures:
            </label>
            <ul style={{ paddingLeft: "20px" }}>
              {(carData.pictures || []).map((picture, index) => (
                <li key={index}>
                  <img
                    src={picture}
                    alt={`Car Picture ${index + 1}`}
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "5px",
                      marginRight: "10px",
                    }}
                  />
                </li>
              ))}
            </ul>
            <input
              type="file"
              accept="image/*"
              onChange={handlePictureUpload}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                marginTop: "10px",
              }}
            />
            {uploading && <p>Uploading...</p>}
          </div>

          <button
            type="submit"
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#28a745",
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "5px",
              border: "none",
              cursor: "pointer",
            }}
          >
            Save Changes
          </button>
        </form>
      ) : (
        <p style={{ textAlign: "center", fontSize: "1.2em", color: "black" }}>
          Please select a car to edit.
        </p>
      )}
    </div>
  );
};

export default EditCarDetails;
